import GoogleMapReact, { fitBounds } from 'google-map-react';
import { Box } from 'native-base';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { MapProps } from 'app/components/Map';
import MapLocation from 'app/components/MapLocation';

const GOOGLE_MAPS_WEB_API_KEY = 'AIzaSyBAMgAQ-Zq6aejhA93V4WDy8JdBjIHVzYY';

export default function MapWeb({
  markers = [],
  zoom,
  ratio = 5 / 3,
  reset,
  onReset,
  onPan,
}: MapProps) {
  const mapRef = useRef<GoogleMapReact | null>(null);
  const [width, setWidth] = useState(0);
  const { center: initialCenter, zoom: $zoom } = useMemo(
    () => calculateCenterZoom({ coords: markers, width, height: width * ratio }),
    [markers, width, ratio]
  );
  const [center, setCenter] = useState(initialCenter);

  useEffect(() => {
    if (reset) {
      setCenter(initialCenter);
      if (onReset) onReset();
    }
  }, [initialCenter, onReset, reset]);

  const zoomOverride = markers.length > 1 ? 11 : $zoom || zoom;

  return (
    <Box
      style={{ aspectRatio: ratio }}
      testID="Map"
      w="full"
      onLayout={(e) => setWidth(e.nativeEvent.layout.width)}
    >
      <GoogleMapReact
        ref={(r) => {
          mapRef.current = r;
        }}
        bootstrapURLKeys={{ key: GOOGLE_MAPS_WEB_API_KEY }}
        center={center}
        options={mapOptions}
        zoom={zoomOverride}
        onDrag={onPan}
        onDragEnd={({ center: { lat, lng } }) => {
          setCenter({ lat: lat(), lng: lng() });
        }}
      >
        {markers.filter(Boolean).map(({ title, latitude, longitude, ...marker }) => (
          <MapLocation
            key={`${title}_${latitude},${longitude}`}
            lat={latitude}
            lng={longitude}
            title={title}
            withOffset
            {...marker}
          />
        ))}
      </GoogleMapReact>
    </Box>
  );
}

function calculateCenterZoom({
  coords = [],
  width,
  height,
}: {
  coords: {
    latitude: number;
    longitude: number;
  }[];
  width: number;
  height: number;
}) {
  const SAFETY_MARGIN = 0.005;
  const bounds = coords.reduce(
    (acc, { latitude: lat, longitude: lng }) => {
      acc.lat.min = acc.lat.min ? Math.min(acc.lat.min, lat) : lat;
      acc.lat.max = acc.lat.max ? Math.max(acc.lat.max, lat) : lat;
      acc.lng.min = acc.lng.min ? Math.min(acc.lng.min, lng) : lng;
      acc.lng.max = acc.lng.max ? Math.max(acc.lng.max, lng) : lng;
      return acc;
    },
    {
      lat: {
        min: 0,
        max: 0,
      },
      lng: {
        min: 0,
        max: 0,
      },
    }
  );

  return fitBounds(
    {
      nw: { lat: bounds.lat.max + SAFETY_MARGIN, lng: bounds.lng.min - SAFETY_MARGIN },
      ne: { lat: bounds.lat.max + SAFETY_MARGIN, lng: bounds.lng.max + SAFETY_MARGIN },
      se: { lat: bounds.lat.min - SAFETY_MARGIN, lng: bounds.lng.max + SAFETY_MARGIN },
      sw: { lat: bounds.lat.max + SAFETY_MARGIN, lng: bounds.lng.min - SAFETY_MARGIN },
    },
    { width, height }
  );
}

function mapOptions(maps: GoogleMapReact.Maps): GoogleMapReact.MapOptions {
  return {
    panControl: false,
    scrollwheel: false,
    mapTypeControl: false,
    zoomControl: true,
    zoomControlOptions: {
      position: maps.ControlPosition.LEFT_CENTER,
    },
    streetViewControl: true,
    streetViewControlOptions: {
      position: maps.ControlPosition.LEFT_CENTER,
    },
  };
}
